"use client";

import type { LoopRange } from "./measure-grid";

interface LoopActionBarProps {
  /** La plage sélectionnée. Sélectionner n'est pas boucler. */
  loop: LoopRange | null;
  onClear: () => void;
  /** Le transport tourne-t-il sur la sélection ? */
  isLooping?: boolean;
  onToggleLooping?: () => void;
}

export function LoopActionBar({
  loop,
  onClear,
  isLooping = false,
  onToggleLooping,
}: LoopActionBarProps) {
  // La barre n'existe que quand une sélection existe. Sans ça elle occupait
  // 48 px en permanence pour deux boutons inertes, et ces pixels manquaient
  // à la liste des parties juste au-dessus.
  if (loop === null) {
    return null;
  }

  const single = loop.startMeasure === loop.endMeasure;

  const range = single
    ? `${loop.startMeasure}`
    : `${loop.startMeasure}–${loop.endMeasure}`;

  const loopLabel = single
    ? `Boucler la mesure ${loop.startMeasure}`
    : `Boucler les mesures ${loop.startMeasure} à ${loop.endMeasure}`;

  return (
    <div className="px-2 py-2 border-t border-border-subtle flex items-center gap-1.5">
      {/* La croix seule : à deux libellés, les 184 px utiles de la barre
          laissaient au bouton de droite moins que sa propre ligne, et
          « Boucler 15-22 » se cassait en deux. L'action principale garde
          ses mots, celle-ci se contente de son infobulle. */}
      <button
        type="button"
        onClick={onClear}
        title="Retirer la sélection"
        aria-label="Retirer la sélection"
        className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md transition-all duration-150 cursor-pointer bg-bg-elevated text-text-secondary hover:bg-bg-hover hover:text-text-primary"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <path d="M3 3l6 6M9 3L3 9" />
        </svg>
      </button>

      <button
        type="button"
        onClick={onToggleLooping}
        aria-pressed={isLooping}
        title={isLooping ? "Arrêter de boucler" : loopLabel}
        className={`flex-1 flex items-center justify-center gap-1.5 h-8 px-2 rounded-md text-[11px] font-semibold whitespace-nowrap transition-all duration-150 cursor-pointer ${
          isLooping
            ? "bg-accent text-white shadow-[0_0_12px_rgba(139,92,246,0.3)]"
            : "bg-accent/20 text-accent-bright hover:bg-accent/30"
        }`}
      >
        <svg
          className="shrink-0"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        >
          <path d="M1.5 5h9M8.5 3l2 2-2 2" />
          <path d="M12.5 9h-9M5.5 7l-2 2 2 2" />
        </svg>
        Boucler
        <span className="text-[10px] opacity-75 font-normal tabular-nums ml-0.5">
          {range}
        </span>
      </button>
    </div>
  );
}
